import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActualIncidentId } from './commonHooks';
import { useSelector } from 'react-redux';
import { selectUserGuid } from '../../../slices/commonSelectors';
import config from '../../../constants/apiConfig';
import {
  keys as actionKeys,
  CreateActionReqData,
  useCreateAction,
} from './actionHooks';
import { toast } from 'react-toastify';
import { ActionBase } from '../../../types/iapBaseTypes';
import { toUtcIso } from '../../../utils/dateTime';
import { IapScope, INCIDENT } from '../ICSConstants';

export type IncidentActionBase = ActionBase & {
  id: UUID;
  action_id: UUID;
  poc: string | null;
  briefed_poc: boolean;
  start_date: Date | null;
  status: string | null;
  target_date: Date | null;
  actual_date: Date | null;
  resources?: UUID[];
  utc_offset?: number;
  parent_id?: UUID | null;
};

export type EditIncidentActionRequest = IncidentActionBase;

export type GetIncidentActionResponse = IncidentActionBase & {
  group_guid: UUID;
  incident_id: UUID;
  user_guid: UUID;
  resource_details?: any[];
  user_name?: string;
  poc_user_name?: string;
  version: number;
  timestamp: Date;
  index?: number;
};

export const keys = {
  base: ['incident_actions'] as const,
  byIncidentAndUser: (incidentId: string, userId: string) =>
    [...keys.base, incidentId, userId] as const,
} as const;

// Fetch all actions
export const useIncidentActions = (scope: IapScope = INCIDENT) => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.byIncidentAndUser(incidentId, user_guid),
    queryFn: async () => {
      const resp = await axios.get<GetIncidentActionResponse[]>(
        `${config.apiGateway.ics_incident_actions}/${incidentId}`
      );
      return resp.data.map((a) => ({
        ...a,
        start_date: toUtcIso(
          a.start_date
        ) as GetIncidentActionResponse['start_date'],
        target_date: toUtcIso(
          a.target_date
        ) as GetIncidentActionResponse['target_date'],
        actual_date: toUtcIso(
          a.actual_date
        ) as GetIncidentActionResponse['actual_date'],
        timestamp: toUtcIso(
          a.timestamp
        ) as GetIncidentActionResponse['timestamp'],
      }));
    },
    enabled: scope === INCIDENT,
  });
};

export const useCreateIncidentAction = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useCreateAction();
  return {
    ...mutation,
    mutateAsync: (data: CreateActionReqData) => {
      data.incident_id = incidentId;
      return mutation.mutateAsync(data, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: actionKeys.all });
          queryClient.invalidateQueries({
            queryKey: keys.byIncidentAndUser(incidentId, user_guid),
          });
        },
      });
    },
  };
};

export const useUpdateIncidentAction = () => {
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditIncidentActionRequest) => {
      // Ensure archived field is included with a default value
      const updateData = {
        ...data,
        archived: data.archived !== undefined ? data.archived : false,
        user_guid,
      };
      return await axios.put(
        `${config.apiGateway.ics_incident_actions}/${incidentId}/${data.id}`,
        updateData
      );
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<GetIncidentActionResponse[]>(
        keys.byIncidentAndUser(incidentId, user_guid),
        (old) =>
          old?.map((a) => (a.id === variables.id ? { ...a, ...variables } : a))
      );
      queryClient.invalidateQueries({ queryKey: actionKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
